/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import { FC, useEffect, useState } from "react";
import { MdPlayArrow } from "react-icons/md";
import ReactPlayer from "react-player";
import useStopCurrentRunningVideoAudio from "~/hook/useStopCurrentRunningVideoAudio";
import { useVideoManager } from "~/hook/useVideoManager";
import { FALLBACK_IMAGE_URL, youtubeLinkPrefix } from "~/utils/constant";
import ClientOnly from "./ClentOnly";
import ImageWithBlurHash from "./ImageWithBlurHash";
import LightGalleryWrapper from "./LightGalleryWrapper";

type ImageType = {
  id: number;
  url: {
    small: string;
    medium: string;
    large: string;
    hd: string;
    original: string;
  };
  blur_hash: string;
  width: number;
  height: number;
  alt: string;
  video_id: string | null;
};

type RowGalleryProps = {
  imageData: ImageType[];
  rowHeight?: number; // px, default 200
  className?: string;
};

const RowGallery: FC<RowGalleryProps> = ({ imageData, rowHeight = 200, className = "" }) => {
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  // Log when images are filtered for lightgallery
  const filteredImagesForGallery = imageData
    .filter((img) => img.video_id === null)
    .map((img) => ({
      id: img.id,
      url: img.url
        ? {
            small: img.url.small,
            medium: img.url.medium,
            large: img.url.large,
            hd: img.url.hd,
            original: img.url.original,
          }
        : {
            small: FALLBACK_IMAGE_URL,
            medium: FALLBACK_IMAGE_URL,
            large: FALLBACK_IMAGE_URL,
            hd: FALLBACK_IMAGE_URL,
            original: FALLBACK_IMAGE_URL,
          },
      photoName: img.alt || "Daily Darshan Image",
    }));

  // Create a map of original index to filtered index
  const originalIndexToFilteredIndex = new Map<number, number>();
  imageData.forEach((img, originalIndex) => {
    if (img.video_id === null) {
      const filteredIndex = originalIndexToFilteredIndex.size;
      originalIndexToFilteredIndex.set(originalIndex, filteredIndex);
      console.log(`Mapped original index ${originalIndex} to filtered index ${filteredIndex}`);
    }
  });

  // Use video manager hook with unique component ID based on imageData
  const componentId = `row-gallery-${imageData.length}-${imageData[0]?.id || "empty"}`;
  const { playVideo, pauseVideo, isVideoPlaying, pauseAllVideos } = useVideoManager(componentId);
  const { removeVideoPlay } = useStopCurrentRunningVideoAudio();

  const preloadImages = (imageUrls: string[]): Promise<void> => {
    return Promise.all(
      imageUrls.map((url) => {
        return new Promise((resolve) => {
          const img = new Image();
          img.src = url;
          img.onload = () => resolve(FALLBACK_IMAGE_URL);
          img.onerror = () => resolve(FALLBACK_IMAGE_URL); // Resolve on error to avoid blocking
        });
      }),
    ).then(() => {});
  };

  useEffect(() => {
    const imageUrls = imageData.map((image) => image.url.small);
    preloadImages(imageUrls);

    return () => {
      pauseAllVideos();
    };
  }, [imageData]);

  return (
    <div className="w-full">
      <div className={`w-full flex gap-2 p-2 ${className}`} style={{ alignItems: "flex-start" }}>
        <>
          {imageData.map((image, index) => {
            return (
              <div
                key={index}
                className={`rounded-lg overflow-hidden shadow-md ${image.video_id ? "" : "cursor-zoom-in"}`}
                style={{
                  height: rowHeight,
                  width: image.video_id ? "" : image.width,
                  flex: "0 0 auto",
                  display: "flex",
                  alignItems: "center",
                  aspectRatio: image.video_id ? "16/9" : "",
                }}
                {...(image.video_id
                  ? {}
                  : {
                      onClick: () => {
                        const filteredIndex = originalIndexToFilteredIndex.get(index);
                        if (filteredIndex !== undefined) {
                          setLightboxIndex(filteredIndex);
                        }
                      },
                      onKeyDown: (e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          const filteredIndex = originalIndexToFilteredIndex.get(index);
                          if (filteredIndex !== undefined) {
                            setLightboxIndex(filteredIndex);
                          }
                        }
                      },
                      tabIndex: 0,
                      role: "button",
                      "aria-label": `View image ${image.alt}`,
                    })}
              >
                <ClientOnly
                  fallback={
                    image.video_id ? (
                      <div className="w-full h-full bg-gradient-to-br from-gray-800 to-gray-900 flex items-center justify-center rounded-lg relative">
                        <div className="flex flex-col items-center justify-center gap-4">
                          <div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center animate-pulse">
                            <MdPlayArrow className="w-8 h-8 text-white" />
                          </div>
                          <p className="text-gray-400 text-sm font-medium">Loading player...</p>
                        </div>
                      </div>
                    ) : (
                      <div className={`relative w-full h-full`}>
                        <img
                          src={image.url.medium}
                          loading="lazy"
                          alt={image.alt || "Daily Darshan Image"}
                          className="w-full h-full object-cover rounded-lg shadow-md select-none"
                          onError={({ currentTarget }) => {
                            const fallback = FALLBACK_IMAGE_URL;
                            const initialSrc = image.url.small;
                            if (currentTarget.src.endsWith(initialSrc)) {
                              currentTarget.src = fallback; // or set to ""
                            } else {
                              currentTarget.src = fallback;
                            }

                            currentTarget.onerror = null; // prevents looping
                          }}
                        />
                      </div>
                    )
                  }
                >
                  {image.video_id ? (
                    <div className="aspect-[16/9] h-full">
                      <ReactPlayer
                        key={`video-${image.video_id}`}
                        url={`${youtubeLinkPrefix}${image.video_id}`}
                        width="100%"
                        height="100%"
                        controls
                        playing={isVideoPlaying(image.video_id || "", index)}
                        onPlay={() => {
                          if (image.video_id) {
                            playVideo(image.video_id, index);
                            removeVideoPlay();
                          }
                        }}
                        onPause={() => {
                          // Only call pauseVideo if this was a user-initiated pause
                          // We can detect this by checking if the video is still marked as the active one
                          // If it's not active, then this pause was caused by another video starting
                          if (image.video_id && isVideoPlaying(image.video_id, index)) {
                            pauseVideo(image.video_id, index);
                          }
                        }}
                        onEnded={() => {
                          if (image.video_id) {
                            pauseVideo(image.video_id, index);
                          }
                        }}
                      />
                    </div>
                  ) : (
                    <div className="w-full h-full overflow-hidden">
                      <ImageWithBlurHash
                        data={{
                          id: image.id,
                          url: {
                            small: image.url.small,
                            medium: image.url.medium,
                            large: image.url.large,
                            hd: image.url.hd,
                            original: image.url.original,
                          },
                          width: image.width,
                          height: rowHeight,
                          blur_hash: image.blur_hash,
                          alt: image.alt,
                        }}
                        handleImageClick={(id) => setLightboxIndex(id)}
                      />
                    </div>
                  )}
                </ClientOnly>
              </div>
            );
          })}
          <LightGalleryWrapper images={filteredImagesForGallery} indexToOpen={lightboxIndex} setLightBoxIndex={() => setLightboxIndex(null)} />
        </>
      </div>
    </div>
  );
};

export default RowGallery;
